import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, LogOut, PlusCircle, ShieldCheck, ShieldOff, Trash2, AlertTriangle, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { formatCurrency, API_URL, todayLuanda } from "../../lib/utils";
import { AttendanceManualDialog } from "./AttendanceManualDialog";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }) : "—");
const SRC = { auto: "automática", manual: "manual", checkin: "check-in" };

const StatusBadge = ({ a }) => {
  if (a.status === "absent") return <Badge className={a.justified ? "bg-zinc-500/15 text-zinc-600" : "bg-red-500/15 text-red-600"} data-testid={`att-status-${a.attendance_id}`}>{a.justified ? "Falta justificada" : "Falta"}</Badge>;
  if ((a.late_minutes || 0) > 0) return <Badge className={a.justified ? "bg-zinc-500/15 text-zinc-600" : "bg-amber-500/15 text-amber-700"} data-testid={`att-status-${a.attendance_id}`}>Atraso {a.late_minutes} min{a.justified ? " · justificado" : ""}</Badge>;
  return <Badge className="bg-emerald-500/15 text-emerald-600" data-testid={`att-status-${a.attendance_id}`}>Presente</Badge>;
};

export const AttendanceTab = ({ attendance, summary, isManager, headers, onRefresh, onCheckIn, onCheckOut, userId }) => {
  const [manualOpen, setManualOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const todayIso = todayLuanda();
  const mine = attendance.find((a) => a.user_id === userId && a.date === todayIso);

  const act = async (id, fn, ok) => {
    setBusy(id);
    try { await fn(); toast.success(ok); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setBusy(""); }
  };
  const justify = (a) => act(a.attendance_id, () => axios.put(`${API_URL}/hr/attendance/${a.attendance_id}`, { justified: !a.justified }, { headers, withCredentials: true }), a.justified ? "Justificação removida" : "Registo justificado — não desconta");
  const remove = (a) => { if (window.confirm(`Remover o registo de ${a.employee_name} em ${a.date}?`)) act(a.attendance_id, () => axios.delete(`${API_URL}/hr/attendance/${a.attendance_id}`, { headers, withCredentials: true }), "Registo removido"); };

  const employees = (summary?.employees || []).map((e) => ({ user_id: e.user_id, name: e.name }));
  const totals = (summary?.employees || []).reduce((t, e) => ({ abs: t.abs + e.absences, late: t.late + e.late_count, ded: t.ded + e.deduction_total }), { abs: 0, late: 0, ded: 0 });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onCheckIn} disabled={!!mine?.check_in} data-testid="checkin-btn"><LogIn className="w-4 h-4 mr-2" /> Check-in</Button>
        <Button variant="outline" onClick={onCheckOut} disabled={!mine?.check_in || !!mine?.check_out} data-testid="checkout-btn"><LogOut className="w-4 h-4 mr-2" /> Check-out</Button>
        {mine && (
          <span className="text-sm text-muted-foreground" data-testid="my-today-status">
            Hoje: entrada <b>{fmtTime(mine.check_in)}</b> · saída <b>{fmtTime(mine.check_out)}</b> {mine.late_minutes > 0 && <span className="text-amber-700">· atraso {mine.late_minutes} min</span>}
          </span>
        )}
        {summary && <span className="ml-auto text-xs text-muted-foreground">Horário {summary.work_start}–{summary.work_end} · tolerância {summary.tolerance} min</span>}
        {isManager && <Button size="sm" variant="outline" onClick={() => setManualOpen(true)} data-testid="attendance-manual-btn"><PlusCircle className="w-4 h-4 mr-1" /> Registar falta/atraso</Button>}
      </div>

      {isManager && summary && !summary.active && (
        <p className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 p-2 flex items-center gap-2" data-testid="attendance-inactive-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" /> As faltas automáticas estão desligadas — só os atrasos ficam registados no check-in.
          <Link to="/settings" className="underline inline-flex items-center gap-1"><Settings2 className="w-3.5 h-3.5" /> Activar em Definições › Empresa</Link>
        </p>
      )}

      {isManager && summary && (
        <Card data-testid="attendance-summary">
          <CardContent className="p-0">
            <div className="flex flex-wrap gap-4 p-3 text-sm border-b">
              <span className="font-semibold">Resumo do mês</span>
              <span>Faltas: <b className="text-red-600">{totals.abs}</b></span>
              <span>Atrasos: <b className="text-amber-700">{totals.late}</b></span>
              <span>Desconto estimado: <b className="font-mono">{formatCurrency(totals.ded)}</b></span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-muted-foreground text-left"><th className="p-2 pl-3">Funcionário</th><th className="p-2 text-center">Dias</th><th className="p-2 text-center">Faltas</th><th className="p-2 text-center">Atrasos</th><th className="p-2 text-right">Desc. faltas</th><th className="p-2 text-right pr-3">Desc. atrasos</th></tr></thead>
                <tbody>
                  {summary.employees.filter((e) => e.role !== "admin" || e.present_days || e.absences).map((e) => (
                    <tr key={e.user_id} className="border-t" data-testid={`att-summary-${e.user_id}`}>
                      <td className="p-2 pl-3 font-medium">{e.name}{e.rules?.track === false && <span className="text-[10px] text-muted-foreground ml-1">(sem controlo)</span>}</td>
                      <td className="p-2 text-center">{e.present_days}</td>
                      <td className={`p-2 text-center ${e.absences ? "text-red-600 font-semibold" : ""}`}>{e.absences}{e.justified_absences ? <span className="text-xs text-muted-foreground"> (+{e.justified_absences} just.)</span> : null}</td>
                      <td className={`p-2 text-center ${e.late_count ? "text-amber-700 font-semibold" : ""}`}>{e.late_count}{e.late_minutes ? <span className="text-xs text-muted-foreground"> · {e.late_minutes} min</span> : null}</td>
                      <td className="p-2 text-right font-mono">{e.deduction_absences ? `−${formatCurrency(e.deduction_absences)}` : "—"}</td>
                      <td className="p-2 text-right pr-3 font-mono">{e.deduction_late ? `−${formatCurrency(e.deduction_late)}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 divide-y max-h-[500px] overflow-y-auto" data-testid="attendance-list">
          {attendance.length === 0 && <p className="p-6 text-center text-muted-foreground">Sem registos neste mês</p>}
          {attendance.map((a) => (
            <div key={a.attendance_id} className="flex items-center justify-between p-3 text-sm gap-3 flex-wrap" data-testid={`att-row-${a.attendance_id}`}>
              <div className="min-w-40">
                <p className="font-medium">{a.employee_name}</p>
                <p className="text-xs text-muted-foreground">{a.date}{a.source && a.source !== "checkin" ? ` · ${SRC[a.source] || a.source}` : ""}{a.justification ? ` · ${a.justification}` : ""}</p>
              </div>
              <StatusBadge a={a} />
              <div className="flex items-center gap-4 font-mono text-sm">
                <span>Entrada: <b>{fmtTime(a.check_in)}</b></span>
                <span>Saída: <b>{fmtTime(a.check_out)}</b></span>
              </div>
              {isManager && (
                <div className="flex items-center gap-1">
                  {(a.status === "absent" || a.late_minutes > 0) && (
                    <Button size="sm" variant="ghost" disabled={busy === a.attendance_id} onClick={() => justify(a)} title={a.justified ? "Remover justificação" : "Justificar (não desconta)"} data-testid={`att-justify-${a.attendance_id}`}>
                      {a.justified ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-destructive" disabled={busy === a.attendance_id} onClick={() => remove(a)} title="Remover registo" data-testid={`att-delete-${a.attendance_id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <AttendanceManualDialog open={manualOpen} onOpenChange={setManualOpen} employees={employees} headers={headers} onSaved={onRefresh} />
    </div>
  );
};
